import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Checkbox from 'components/shared/form/Checkbox';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import LocationEpisode from 'models/LocationEpisode';
import { colors } from 'styles/theme/colors';

export type ShareToLinkedRecordsModalProps = {
  linkedRecords: LocationEpisode[];
  currentLocationEpisodeId: string;
  currentLocationEpisodeName: string;
  patientName: string;
  onSubmit: (selectedIds: string[]) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
};

export default function ShareToLinkedRecordsModal({
  linkedRecords,
  currentLocationEpisodeId,
  currentLocationEpisodeName,
  patientName,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ShareToLinkedRecordsModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    return new Set([currentLocationEpisodeId]);
  });

  const allRecords = useMemo(() => {
    const currentRecord = new LocationEpisode({
      id: currentLocationEpisodeId,
      group: { name: currentLocationEpisodeName },
    });
    return [currentRecord, ...linkedRecords];
  }, [currentLocationEpisodeId, currentLocationEpisodeName, linkedRecords]);

  const handleToggle = (locationEpisodeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationEpisodeId)) {
        next.delete(locationEpisodeId);
      } else {
        next.add(locationEpisodeId);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    onSubmit(Array.from(selectedIds));
  };

  return (
    <StandardModal title='Post Note to Multiple Records?' onCancel={onCancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <Instructions>Select below which records to post this note to:</Instructions>
      <RecordList>
        {allRecords.map((link) => (
          <RecordItem key={link.id} onClick={() => handleToggle(link.id)}>
            <Checkbox
              checked={selectedIds.has(link.id)}
              overrideSetValue
              label={link.group.name}
              labelSize='14px'
              labelMargin='16px'
              checkedColor={colors.primaryBlue}
              size={16}
            />
          </RecordItem>
        ))}
      </RecordList>
      <Actions>
        <Button variant='ghost' onClick={onCancel} disabled={isSubmitting}>
          Go Back
        </Button>
        <Button onClick={handleSubmit} disabled={selectedIds.size === 0 || isSubmitting} loading={isSubmitting}>
          Post Note
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Instructions = styled.p`
  margin-bottom: 16px;
  color: var(--black-75);
`;

const RecordList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const RecordItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border: 1px solid ${colors.primaryBlue};
  border-radius: 4px;
  background: ${colors.primaryBlue05};
`;
